import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Loader2, Printer, Check } from "lucide-react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { IDR, dateTime } from "@/lib/format";
import { LangToggle, btnPrimary, btnGhost } from "@/components/Brand";

const MILESTONES = ["selection_received", "editing_complete", "printing_complete", "delivered"];

export default function SummaryPage() {
  const { token } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/gallery/${token}/summary`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  if (error)
    return (
      <div className="min-h-screen bg-[#050505] text-[#F3F3F1] flex items-center justify-center" data-testid="summary-not-found">
        <h1 className="text-3xl tracking-tighter">This summary is not available.</h1>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#707070]">
        <Loader2 className="animate-spin" />
      </div>
    );

  const s = data.summary;
  const invoice = data.invoice;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain" data-testid="summary-page">
      <div className="max-w-4xl mx-auto px-5 lg:px-10 py-10 lg:py-16">
        <div className="flex items-center justify-between mb-8 no-print">
          {data.studio_logo ? (
            <img data-testid="summary-studio-logo" src={data.studio_logo} alt={data.studio_name} className="h-8 w-auto object-contain" />
          ) : (
            <span className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">{data.studio_name}</span>
          )}
          <LangToggle />
        </div>

        <div className="border border-[#2A2A2A] bg-[#0B0B0B] p-6 sm:p-10 lg:p-14">
          <p className="eyebrow mb-4">{t("summary")}</p>
          <p className="font-mono-accent text-xs text-[#D4AF37]">{data.code}</p>
          <h1 data-testid="summary-title" className="mt-2 text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium">
            {data.title}
          </h1>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-y border-[#2A2A2A] py-6">
            <Meta label={t("client_name")} value={data.client_name || "—"} />
            <Meta label={t("submitted")} value={s.submitted_at || "Draft"} />
            <Meta label={t("edits")} value={`${s.edit_count} (+${s.extra_edit_count})`} testId="summary-edit-count" />
            <Meta label={t("prints")} value={s.print_count} testId="summary-print-count" />
          </div>

          <section className="mt-12" data-testid="tracking-panel">
            <h2 className="text-2xl sm:text-3xl tracking-tight">{t("tracking")}</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-3">
              {MILESTONES.map((m) => {
                const done = !!data.milestones?.[m];
                return (
                  <div key={m} data-testid={`track-${m}`}
                    className={`border p-4 ${done ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A]"}`}>
                    <span className={`flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase ${done ? "text-[#D4AF37]" : "text-[#707070]"}`}>
                      {done && <Check size={12} />} {t(`milestone_${m}`)}
                    </span>
                    <span className="mt-2 block font-mono-accent text-[10px] text-[#707070]">
                      {done ? dateTime(data.milestones[m]) : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="mt-14">
            <h2 className="text-2xl sm:text-3xl tracking-tight">{t("edits")}</h2>
            <div className="mt-6 space-y-px bg-[#2A2A2A] border border-[#2A2A2A]" data-testid="summary-edits-list">
              {s.edits.length === 0 && <p className="bg-[#050505] p-6 text-sm text-[#A8A8A8]">—</p>}
              {s.edits.map((item) => (
                <div key={item.file_id} className="bg-[#050505] p-5 flex gap-5">
                  <img src={item.thumb} alt={item.name} className="w-20 h-20 object-cover border border-[#2A2A2A]" />
                  <div className="min-w-0">
                    <p className="font-mono-accent text-xs">
                      {item.name}
                      {item.is_extra && <span className="ml-2 text-[#D4AF37]">({t("extra")})</span>}
                    </p>
                    <p className="mt-2 text-sm text-[#A8A8A8]">{item.note || "—"}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-14">
            <h2 className="text-2xl sm:text-3xl tracking-tight">{t("prints_title")}</h2>
            <div className="mt-6 space-y-px bg-[#2A2A2A] border border-[#2A2A2A]" data-testid="summary-prints-list">
              {s.prints.length === 0 && <p className="bg-[#050505] p-6 text-sm text-[#A8A8A8]">—</p>}
              {s.prints.map((item, i) => (
                <div key={`${item.file_id || item.name}-${item.size}-${i}`} className="bg-[#050505] p-5 flex gap-5">
                  <img src={item.thumb} alt={item.name} className="w-20 h-20 object-cover border border-[#2A2A2A]" />
                  <div className="min-w-0">
                    <p className="font-mono-accent text-xs">{item.name}</p>
                    <p className="mt-2 text-sm text-[#D4AF37]" data-testid={`summary-print-size-${i}`}>
                      {item.size}{Number(item.qty || 1) > 1 ? ` - ${item.qty}pcs` : ""}
                    </p>
                    <p className="mt-1 text-sm text-[#A8A8A8]">
                      {item.frame_code ? `${t("frame_code")}: ${item.frame_code}` : "—"} {item.note}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {Object.keys(s.print_size_breakdown || {}).length > 0 && (
              <p className="mt-5 font-mono-accent text-[11px] text-[#707070]" data-testid="print-breakdown">
                {Object.entries(s.print_size_breakdown).map(([size, qty]) => `${qty} pcs ${size}`).join(" · ")}
              </p>
            )}
          </section>

          {invoice?.items?.length > 0 && (
            <section className="mt-14" data-testid="summary-invoice">
              <h2 className="text-2xl sm:text-3xl tracking-tight">{t("invoice")}</h2>
              <div className="mt-6">
                {invoice.items.map((i) => (
                  <div key={`${i.label}-${i.qty}-${i.amount}`} className="flex justify-between py-2.5 border-b border-[#1E1E1E] text-sm">
                    <span className="text-[#A8A8A8]">{i.label} × {i.qty}</span>
                    <span className="font-mono-accent text-xs">{IDR(i.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between mt-4 pt-3 border-t border-[#D4AF37]/50">
                  <span className="text-[11px] tracking-[0.18em] uppercase">{t("total")}</span>
                  <span data-testid="summary-invoice-total" className="font-mono-accent text-sm text-[#D4AF37]">{IDR(invoice.total)}</span>
                </div>
              </div>
            </section>
          )}

          {s.general_note && (
            <section className="mt-14">
              <h2 className="text-2xl sm:text-3xl tracking-tight">{t("notes")}</h2>
              <p data-testid="summary-general-note" className="mt-5 text-sm text-[#A8A8A8] leading-relaxed border-l-2 border-[#D4AF37] pl-5">
                {s.general_note}
              </p>
            </section>
          )}

          <div className="mt-14 flex flex-wrap gap-4 no-print">
            <a data-testid="summary-download-pdf-btn" href={`${API}/gallery/${token}/summary.pdf`} className={btnPrimary}>
              <Download size={14} /> {t("download_pdf")}
            </a>
            <button data-testid="summary-print-btn" onClick={() => window.print()} className={btnGhost}>
              <Printer size={14} /> {t("print_page")}
            </button>
          </div>
        </div>
      {data.watermark && (
        <div className="mt-10 border border-[#2A2A2A] bg-[#0B0B0B] px-5 py-4 flex items-center justify-between gap-4 no-print" data-testid="summary-watermark">
          <p className="text-xs text-[#707070]">{t("powered_by_body")}</p>
          <a href="/" className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] hover:underline">
            {t("powered_by")}
          </a>
        </div>
      )}
      </div>
    </div>
  );
}

const Meta = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] tracking-[0.18em] uppercase text-[#707070]">{label}</p>
    <p className="mt-1.5 font-mono-accent text-sm">{value}</p>
  </div>
);
